import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, MapPin, Users, ClipboardList, ShieldCheck, FileSignature, ScrollText, HelpCircle, CloudOff, ShieldAlert, AlertTriangle, Map as MapIcon, Megaphone, Activity, MessageCircle, PlayCircle, UserCog, Menu, X } from "lucide-react";
import { subscribeNet, isOnline } from "@/lib/netSim";
import BrandMark from "@/components/BrandMark";
import { readQueue, subscribeQueue } from "@/lib/offlineQueue";
import { api } from "@/lib/api";
import ProfileModal from "@/components/ProfileModal";

const NAV = {
  admin: [
    { to: "/app", label: "Overview", icon: LayoutDashboard },
    { to: "/app/elections", label: "Elections", icon: FileSignature },
    { to: "/app/hierarchy", label: "Hierarchy", icon: MapPin },
    { to: "/app/entry", label: "Data Entry", icon: ClipboardList },
    { to: "/app/approvals", label: "Approvals", icon: ShieldCheck },
    { to: "/app/fraud", label: "Fraud Board", icon: AlertTriangle },
    { to: "/app/incidents", label: "Incidents", icon: ShieldAlert },
    { to: "/app/incidents/map", label: "Incident Map", icon: MapIcon },
    { to: "/app/timeline", label: "Timeline", icon: Activity },
    { to: "/app/broadcast", label: "Broadcast SMS", icon: Megaphone },
    { to: "/app/playbooks", label: "Playbooks", icon: PlayCircle },
    { to: "/app/users", label: "Users", icon: Users },
    { to: "/app/audit", label: "Audit Log", icon: ScrollText },
    { to: "/app/help", label: "Field Guide", icon: HelpCircle },
  ],
  agent: [
    { to: "/app/entry", label: "Data Entry", icon: ClipboardList },
    { to: "/app/incidents", label: "Incidents", icon: ShieldAlert },
    { to: "/app/help", label: "Field Guide", icon: HelpCircle },
  ],
  observer: [
    { to: "/app", label: "Overview", icon: LayoutDashboard },
    { to: "/app/approvals", label: "Approvals", icon: ShieldCheck },
    { to: "/app/fraud", label: "Fraud Board", icon: AlertTriangle },
    { to: "/app/incidents", label: "Incidents", icon: ShieldAlert },
    { to: "/app/incidents/map", label: "Incident Map", icon: MapIcon },
    { to: "/app/timeline", label: "Timeline", icon: Activity },
    { to: "/app/help", label: "Field Guide", icon: HelpCircle },
  ],
  viewer: [
    { to: "/app", label: "Overview", icon: LayoutDashboard },
    { to: "/app/help", label: "Field Guide", icon: HelpCircle },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const isOwner = !!user?.is_owner;
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV[user?.role] || NAV.viewer;

  const [queueCount, setQueueCount] = React.useState(readQueue().length);
  const [online, setOnline] = React.useState(isOnline());
  const [reporting, setReporting] = React.useState(null); // { pct, reported, total, electionName }
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [freeze, setFreeze] = React.useState({ enabled: false });
  const [scheduleOpen, setScheduleOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  React.useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  const [scheduleForm, setScheduleForm] = React.useState({ arm_at: "", thaw_at: "" });
  React.useEffect(() => {
    if (!user) return;
    api.get("/system/freeze").then(r => setFreeze(r.data || { enabled: false })).catch(() => {});
    const id = setInterval(() => {
      api.get("/system/freeze").then(r => setFreeze(r.data || { enabled: false })).catch(() => {});
    }, 20000);
    return () => clearInterval(id);
  }, [user?.id]);
  const toggleFreeze = async () => {
    const next = !freeze.enabled;
    if (next && !confirm("Freeze the system? Every non-admin login will be blocked and every non-admin write will return 423 until you thaw.")) return;
    try {
      const { data } = await api.post("/system/freeze", { enabled: next });
      setFreeze({ enabled: data.enabled, at: new Date().toISOString(), by: user.email });
      toast[next ? "warning" : "success"](next ? "System frozen — non-admins are locked out" : "System thawed — normal operations resumed");
    } catch (e) { toast.error("Could not toggle freeze"); }
  };
  React.useEffect(() => {
    const unsub = subscribeQueue((q) => setQueueCount(q.length));
    const unsubNet = subscribeNet(setOnline);
    return () => { unsub && unsub(); unsubNet(); };
  }, []);

  // Global WebSocket listener for real-time fraud alerts + SMS broadcast toasts (active election only)
  // Also polls reporting % for the active election so the sidebar pill is always live.
  React.useEffect(() => {
    if (!user) return;
    const skipReporting = user?.role === "agent";
    let ws; let cancelled = false; let pollId = null;
    const refreshReporting = async (electionId, electionName) => {
      try {
        const { data } = await api.get(`/elections/${electionId}/collation`, { params: { level: "national", include_pending: true } });
        const reported = data?.meta?.polling_units_reported || 0;
        const total = data?.total_polling_units || 0;
        const pct = total ? Math.min(100, Math.round((reported / total) * 100)) : 0;
        if (!cancelled) setReporting({ pct, reported, total, electionName });
      } catch {}
    };
    (async () => {
      try {
        const { data: elections } = await api.get("/elections");
        const active = elections.find(e => e.status === "active") || elections[0];
        if (!active || cancelled) return;
        if (!skipReporting) {
          refreshReporting(active.id, active.name);
          pollId = setInterval(() => refreshReporting(active.id, active.name), 15000);
        }
        const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/collation/${active.id}?token=${encodeURIComponent(localStorage.getItem("ec_token") || "")}`;
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          let msg = null;
          try { msg = JSON.parse(ev.data); } catch { return; }
          if (!skipReporting && ["result.submit", "result.approve", "result.reject"].includes(msg.type)) {
            refreshReporting(active.id, active.name);
          }
          if (msg.type === "fraud.alert") {
            const tier = msg.tier === "critical" ? "CRITICAL" : "HIGH";
            toast.error(`${tier} fraud alert · ${msg.polling_unit_name || msg.polling_unit_code || "PU"}`, {
              description: `Risk ${msg.risk_score} · ${(msg.flags || []).join(", ")}`,
              duration: 12000,
              action: { label: "Open Fraud Board", onClick: () => nav("/app/fraud") },
              id: `fraud-${msg.result_id}`,
            });
          } else if (msg.type === "broadcast.sms") {
            toast.message(`SMS broadcast → ${msg.audience} (${msg.sent}/${msg.recipients})`, {
              description: msg.message,
              duration: 8000,
              id: `sms-${Date.now()}`,
            });
          }
        };
      } catch {}
    })();
    return () => { cancelled = true; if (pollId) clearInterval(pollId); try { ws && ws.close(); } catch {} };
  }, [user?.id]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {menuOpen && <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-backdrop"/>}
      <aside className={`w-72 max-w-[85vw] lg:w-64 shrink-0 border-r border-white/10 bg-[#0A0A0A] flex flex-col h-[100dvh] lg:h-screen fixed lg:sticky top-0 left-0 z-50 lg:z-auto transition-transform duration-300 ease-out ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <Link to="/" className="flex items-center" data-testid="app-logo-link">
              <BrandMark variant="lockup" size={44} />
            </Link>
            <div className="text-xs text-zinc-500 mt-1 uppercase tracking-[0.2em]">Collation Console</div>
          </div>
          <button onClick={() => setMenuOpen(false)} className="lg:hidden p-2 -mr-2 text-zinc-400 hover:text-white" aria-label="Close menu" data-testid="mobile-nav-close">
            <X size={18}/>
          </button>
        </div>

        {(queueCount > 0 || !online) && (
          <Link to="/app/entry" data-testid="sidebar-queue-pill"
            className={`mx-5 mt-4 px-3 py-2 border flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${!online ? "border-amber-400/50 bg-amber-400/[0.08] text-amber-200 hover:bg-amber-400/[0.14]" : "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-300 hover:bg-emerald-500/[0.12]"}`}>
            <CloudOff size={13}/>
            <span className="flex-1">{!online ? "Offline" : "Queued"}</span>
            {queueCount > 0 && <span className="tabular font-semibold text-white bg-black/30 px-1.5 py-0.5">{queueCount}</span>}
          </Link>
        )}

        {reporting && user?.role !== "agent" && (
          <div className="mx-5 mt-4 border border-white/10 bg-[#111114] p-3" data-testid="sidebar-reporting-pill">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Reporting</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"/>Live
              </div>
            </div>
            <div className="mt-1.5 flex items-baseline gap-1.5">
              <div className="font-display text-2xl tabular text-white" data-testid="sidebar-reporting-pct">{reporting.pct}%</div>
              <div className="text-[10px] text-zinc-500 tabular">of {reporting.total.toLocaleString()} PU</div>
            </div>
            <div className="mt-2 h-1 bg-white/5 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-700" style={{ width: `${reporting.pct}%` }} />
            </div>
            <div className="mt-1.5 text-[10px] text-zinc-500 tabular truncate">{reporting.reported.toLocaleString()} reported</div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-4">
          {items.map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to || (to !== "/app" && loc.pathname.startsWith(to));
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${active ? "border-white bg-white/5 text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.03]"}`}>
                <Icon size={16} strokeWidth={1.5} />
                <span>{label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("open-support-chat"))}
            data-testid="nav-live-support"
            className="w-full text-left flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 border-transparent text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/[0.05] transition-colors">
            <MessageCircle size={16} strokeWidth={1.5} />
            <span>Live Support</span>
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </button>
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em]">Signed in</div>
          <div className="text-sm font-medium truncate mt-1" data-testid="user-email">{user?.email}</div>
          <div className="text-xs text-zinc-500 mt-0.5 uppercase">{user?.role === "observer" ? "Reviewer" : user?.role}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {user?.role === "admin" && (
              <button
                onClick={() => setProfileOpen(true)}
                data-testid="edit-profile-btn"
                className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] py-2 border border-white/10 hover:bg-white/10 transition-colors">
                <UserCog size={12}/> Profile
              </button>
            )}
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className={`${user?.role !== "admin" ? "col-span-2" : ""} flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] py-2 border border-white/10 hover:bg-white/10 transition-colors`}>
              <LogOut size={12}/> Logout
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center gap-3" data-testid="mobile-topbar">
          <button onClick={() => setMenuOpen(true)} className="p-2 -ml-2 text-zinc-200 hover:text-white" aria-label="Open menu" data-testid="mobile-nav-open">
            <Menu size={22}/>
          </button>
          <Link to="/" className="flex items-center"><BrandMark variant="lockup" size={30} /></Link>
          <div className="ml-auto flex items-center gap-2">
            {(queueCount > 0 || !online) && <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${!online ? "bg-amber-400/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{!online ? "Offline" : `${queueCount} queued`}</span>}
            {freeze.enabled && <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-red-600 text-white">Frozen</span>}
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:inline">{user?.role === "observer" ? "Reviewer" : user?.role}</span>
          </div>
        </div>
        {freeze.enabled && (
          <div className="bg-red-600 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-3 flex-wrap" data-testid="freeze-banner">
            <ShieldAlert size={14}/>
            <span>System frozen for election day · non-admins are locked out</span>
            {freeze.by && <span className="opacity-70">· by {freeze.by}</span>}
            {freeze.thaw_at && <span className="opacity-70">· auto-thaw {String(freeze.thaw_at).replace("T"," ").slice(0,16)}</span>}
            {isOwner && (
              <button onClick={toggleFreeze} data-testid="freeze-thaw-btn"
                className="ml-2 px-3 py-1 bg-black text-white border border-white/40 hover:bg-white/10 text-[10px] uppercase tracking-[0.2em]">
                Thaw system
              </button>
            )}
          </div>
        )}
        {isOwner && !freeze.enabled && (
          <div className="bg-[#0F0F12] border-b border-white/5 px-4 lg:px-5 py-2 flex items-center justify-end gap-2 lg:gap-3 flex-wrap" data-testid="freeze-strip">
            {freeze.arm_at && (
              <span className="text-[10px] uppercase tracking-[0.2em] text-red-300" data-testid="freeze-scheduled">
                Freeze scheduled for {String(freeze.arm_at).replace("T"," ").slice(0,16)}
              </span>
            )}
            <button onClick={()=>setScheduleOpen(true)} data-testid="freeze-schedule-btn"
              className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 px-3 py-1 flex items-center gap-1.5">
              <PlayCircle size={11}/> Schedule
            </button>
            <button onClick={toggleFreeze} data-testid="freeze-enable-btn"
              className="text-[10px] uppercase tracking-[0.2em] text-red-300 hover:text-red-200 border border-red-500/30 hover:border-red-500/60 px-3 py-1 flex items-center gap-1.5">
              <ShieldAlert size={11}/> Election-day freeze · OFF
            </button>
          </div>
        )}
        {queueCount > 0 && (
          <div className="bg-amber-400 text-black px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-3 flex-wrap" data-testid="offline-banner">
            <CloudOff size={14}/>
            <span>{queueCount} submission{queueCount===1?"":"s"} waiting to sync</span>
            {online ? <span className="text-emerald-800">· Reconnecting…</span> : <span>· You're offline</span>}
            {online && (
              <button
                onClick={async () => {
                  try {
                    const { flushQueue } = await import("@/lib/offlineQueue");
                    const out = await flushQueue((await import("@/lib/api")).api);
                    if (out.synced) toast.success(`${out.synced} synced${out.duplicate?` · ${out.duplicate} were already on server`:""}`);
                    else if (out.duplicate) toast.info(`${out.duplicate} already on server — queue cleaned`);
                    if (out.failed) toast.warning(`${out.failed} still queued — will retry`);
                  } catch (e) { toast.error("Sync failed"); }
                }}
                data-testid="banner-sync-now-btn"
                className="ml-2 px-3 py-1 bg-black text-amber-400 border border-black/40 hover:bg-black/90 text-[10px] uppercase tracking-[0.2em]">
                Sync now
              </button>
            )}
          </div>
        )}
        {children}
      </main>
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)}/>}
      {scheduleOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="freeze-schedule-modal">
          <div className="w-full max-w-md border border-white/10 bg-[#0A0A0A] p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Election-day freeze</div>
            <div className="text-lg font-medium mt-1">Schedule automatic freeze</div>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">Set a time to auto-arm the freeze and a time to auto-thaw. Either can be left blank. Times are your local timezone and are checked every 30 seconds.</p>
            <label className="block mt-4"><div className="text-[10px] uppercase tracking-[0.2em] text-red-300">Auto-arm at</div>
              <input type="datetime-local" value={scheduleForm.arm_at} onChange={e=>setScheduleForm(f=>({...f, arm_at: e.target.value}))}
                data-testid="freeze-arm-input"
                className="w-full mt-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm text-white"/></label>
            <label className="block mt-3"><div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Auto-thaw at</div>
              <input type="datetime-local" value={scheduleForm.thaw_at} onChange={e=>setScheduleForm(f=>({...f, thaw_at: e.target.value}))}
                data-testid="freeze-thaw-input"
                className="w-full mt-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm text-white"/></label>
            <div className="mt-4 flex items-center gap-2 justify-end flex-wrap">
              <button onClick={async ()=>{
                try {
                  const { data } = await api.post("/system/freeze", { clear_schedule: true });
                  setFreeze(f => ({ ...f, arm_at: null, thaw_at: null }));
                  toast.success("Schedule cleared"); setScheduleOpen(false);
                } catch (e) { toast.error("Failed"); }
              }} data-testid="freeze-clear-btn" className="px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.2em] hover:bg-white/10">Clear schedule</button>
              <button onClick={()=>setScheduleOpen(false)} className="px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button onClick={async ()=>{
                try {
                  const body = { arm_at: scheduleForm.arm_at ? new Date(scheduleForm.arm_at).toISOString() : null,
                                 thaw_at: scheduleForm.thaw_at ? new Date(scheduleForm.thaw_at).toISOString() : null };
                  const { data } = await api.post("/system/freeze", body);
                  setFreeze({ enabled: data.enabled, arm_at: data.arm_at, thaw_at: data.thaw_at });
                  toast.success("Schedule saved"); setScheduleOpen(false);
                } catch (e) { toast.error("Failed to save"); }
              }} data-testid="freeze-save-schedule-btn" className="px-3 py-1.5 bg-emerald-500 text-black text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400">Save schedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
